import React, { useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { dataService } from '../services/api';
import type { DataFeature } from '../types';
import {
  encryptFeatureListToBase64,
  featureLikeToFloat64,
  type FeatureLike,
} from '../utils/encryptionV2';

interface AddEntryProps {
  recordId: number;
  features?: DataFeature[];
  isEncrypted: boolean;
  onSuccess?: (symbol: string) => void;
}

const AddEntry: React.FC<AddEntryProps> = ({
  recordId,
  features = [],
  isEncrypted,
  onSuccess,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [asOfTimestamp, setAsOfTimestamp] = useState('');
  const [requestId, setRequestId] = useState('');
  const [dataValues, setDataValues] = useState<string[]>([]);
  const [encryptionKey, setEncryptionKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const featureInputs = useMemo(
    () => features.map((feature, index) => ({
      feature,
      value: dataValues[index] ?? '',
    })),
    [dataValues, features]
  );

  const resetForm = () => {
    setSymbol('');
    setTimestamp('');
    setAsOfTimestamp('');
    setRequestId('');
    setDataValues([]);
    setEncryptionKey('');
  };

  const parseNumberField = (value: string, label: string) => {
    const parsed = Number(value);
    if (!value.trim() || Number.isNaN(parsed)) {
      throw new Error(`${label} must be a valid number`);
    }
    return parsed;
  };

  const normalizeDataValue = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') return trimmed;

    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;

    const parsedNumber = Number(trimmed);
    if (!Number.isNaN(parsedNumber)) {
      return parsedNumber;
    }

    return trimmed;
  };

  const toFloat64List = (values: string[]) => {
    const normalized = values.map((v) => normalizeDataValue(v) as FeatureLike);
    return normalized.map((v, idx) => featureLikeToFloat64(v, idx));
  };

  const handleFeatureChange = (index: number, value: string) => {
    setDataValues((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleAdd = async () => {
    setIsLoading(true);
    try {
      const trimmedSymbol = symbol.trim();
      if (!trimmedSymbol) {
        throw new Error('Symbol is required');
      }

      const parsedTimestamp = parseNumberField(timestamp, 'Timestamp');

      const payload: Record<string, unknown> = {
        record_id: recordId,
        symbol: trimmedSymbol,
        timestamp: parsedTimestamp,
      };

      const trimmedAsOfTimestamp = asOfTimestamp.trim();
      if (trimmedAsOfTimestamp) {
        payload.as_of_timestamp = parseNumberField(trimmedAsOfTimestamp, 'As Of Timestamp');
      }

      const trimmedRequestId = requestId.trim();
      if (trimmedRequestId) {
        payload.request_id = trimmedRequestId;
      }

      if (isEncrypted) {
        if (features.length === 0) {
          throw new Error('No features are configured for this record');
        }

        const trimmedKey = encryptionKey.trim();
        if (!trimmedKey) {
          throw new Error('Encryption Key is required for encrypted records');
        }

        const hasMissingValues = featureInputs.some(({ value }) => !value.trim());
        if (hasMissingValues) {
          throw new Error('Please fill in every data field');
        }

        const floats = toFloat64List(featureInputs.map(({ value }) => value));
        if (floats.length !== features.length) {
          throw new Error('Data field count does not match configured features');
        }

        payload.encrypted_payload = await encryptFeatureListToBase64(trimmedKey, floats);
      } else {
        if (features.length === 0) {
          throw new Error('No features are configured for this record');
        }

        const hasMissingValues = featureInputs.some(({ value }) => !value.trim());
        if (hasMissingValues) {
          throw new Error('Please fill in every data field');
        }

        const data = featureInputs.map(({ value }) => normalizeDataValue(value));
        if (data.length !== features.length) {
          throw new Error('Data field count does not match configured features');
        }

        payload.data = data;
      }

      await dataService.addEntry(payload);
      toast({
        title: 'Entry added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleClose();
      onSuccess?.(trimmedSymbol);
    } catch (error) {
      toast({
        title: 'Failed to add entry',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        leftIcon={<Icon as={FaPlus} />}
        colorScheme="teal"
        onClick={() => setIsModalOpen(true)}
        size="md"
        fontWeight="semibold"
        boxShadow="sm"
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        }}
        transition="all 0.2s"
      >
        Add Entry
      </Button>

      <Modal isOpen={isModalOpen} onClose={handleClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Symbol</FormLabel>
                  <Input
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    placeholder="Enter symbol"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Timestamp</FormLabel>
                  <Input
                    value={timestamp}
                    onChange={(e) => setTimestamp(e.target.value)}
                    placeholder="e.g. 1776048456000"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>As Of Timestamp</FormLabel>
                  <Input
                    value={asOfTimestamp}
                    onChange={(e) => setAsOfTimestamp(e.target.value)}
                    placeholder="e.g. 1776048456000"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Batch ID</FormLabel>
                  <Input
                    value={requestId}
                    onChange={(e) => setRequestId(e.target.value)}
                    placeholder="Optional"
                  />
                </FormControl>
              </SimpleGrid>

              <VStack spacing={3} align="stretch">
                <Text fontWeight="semibold">Data Fields</Text>
                {featureInputs.length > 0 ? (
                  featureInputs.map(({ feature, value }, index) => (
                    <FormControl key={`${feature.name}-${index}`} isRequired>
                      <FormLabel>{feature.name}</FormLabel>
                      <Input
                        value={value}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        placeholder={feature.description || 'Enter value'}
                      />
                    </FormControl>
                  ))
                ) : (
                  <Text color="gray.500">No features configured for this record.</Text>
                )}

                {isEncrypted && (
                  <FormControl isRequired>
                    <FormLabel>Encryption Key</FormLabel>
                    <Input
                      type="password"
                      value={encryptionKey}
                      onChange={(e) => setEncryptionKey(e.target.value)}
                      placeholder="Key used for local encryption (will not be submitted)"
                      autoComplete="off"
                    />
                  </FormControl>
                )}
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAdd}
              isLoading={isLoading}
            >
              Add Entry
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddEntry; 
